import React from "react";
import { Box, Paper, Typography, IconButton, Tooltip } from "@mui/material";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MiniDoubleSlider from '../../shared/Sliders/MiniDoubleSlider';
import { PreferenceDistributionData } from '../../interfaces/DTO/contactPreferences';
import { NewExposureMap } from '../../hooks/feeLab/usePathDragChanges';

type MetricKey = 'stock_market_percent' | 'foreign_currency_exposure_percent' | 'foreign_exposure_percent';

interface Props {
  stockExposure: number;
  currencyExposure: number;
  foreignExposure: number;
  /** Original (pre-drag) values for slider triangle position */
  originalStockExposure?: number;
  originalCurrencyExposure?: number;
  originalForeignExposure?: number;
  hasStockChanges?: boolean;
  hasCurrencyChanges?: boolean;
  hasForeignChanges?: boolean;
  onMetricClick?: (metric: MetricKey) => void;
  activeMetric?: MetricKey | null;
  isDragEnabled?: boolean;
  preferenceData?: PreferenceDistributionData;
  onPreferenceChange?: (newData: PreferenceDistributionData) => void;
  newExposureMap?: NewExposureMap;
}

const PathDragExposureSummary: React.FC<Props> = ({
  stockExposure,
  currencyExposure,
  foreignExposure,
  originalStockExposure,
  originalCurrencyExposure,
  originalForeignExposure,
  hasStockChanges = false,
  hasCurrencyChanges = false,
  hasForeignChanges = false,
  onMetricClick,
  activeMetric,
  isDragEnabled = false,
  preferenceData,
  onPreferenceChange,
  newExposureMap,
}) => {
  // Compute per-metric average requested new value from newExposureMap
  const getRequestedAvg = (field: MetricKey): number | null => {
    if (!newExposureMap) return null;
    const entries = Object.values(newExposureMap)
      .map(e => e[field]?.new_value)
      .filter((v): v is number => v !== undefined);
    if (entries.length === 0) return null;
    return entries.reduce((s, v) => s + v, 0) / entries.length;
  };

  const renderChangeStatus = (field: MetricKey, actualPercent: number) => {
    const requested = getRequestedAvg(field);
    if (requested === null) return null;
    const matched = Math.abs(actualPercent - requested) <= 10;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
        {matched
          ? <CheckIcon sx={{ fontSize: 14, color: '#2e7d32' }} />
          : <CloseIcon sx={{ fontSize: 14, color: '#c62828' }} />}
        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: matched ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
          {requested.toFixed(0)}%
        </Typography>
      </Box>
    );
  };
  // Helper to convert indices to min/max percentages
  const getRange = (indices: number[] | undefined): { min: number; max: number } => {
    if (!indices || indices.length === 0) return { min: 0, max: 100 };
    const minIndex = Math.min(...indices);
    const maxIndex = Math.max(...indices);
    return {
      min: minIndex * 10,
      max: (maxIndex + 1) * 10,
    };
  };

  // Helper to convert min/max percentages back to indices
  const rangeToIndices = (min: number, max: number): number[] => {
    const minIndex = Math.floor(min / 10);
    const maxIndex = Math.floor(max / 10);
    const indices: number[] = [];
    for (let i = minIndex; i <= maxIndex; i++) {
      if (i >= 0 && i <= 9) {
        indices.push(i);
      }
    }
    return indices;
  };

  const handleStockRangeChange = (min: number, max: number) => {
    if (!onPreferenceChange || !preferenceData) return;
    const newIndices = rangeToIndices(min, max);
    onPreferenceChange({
      ...preferenceData,
      stockExposure: newIndices,
    });
  };

  const handleCurrencyRangeChange = (min: number, max: number) => {
    if (!onPreferenceChange || !preferenceData) return;
    const newIndices = rangeToIndices(min, max);
    onPreferenceChange({
      ...preferenceData,
      currencyExposure: newIndices,
    });
  };

  const handleForeignRangeChange = (min: number, max: number) => {
    if (!onPreferenceChange || !preferenceData) return;
    const newIndices = rangeToIndices(min, max);
    onPreferenceChange({
      ...preferenceData,
      foreignExposure: newIndices,
    });
  };

  const stockRange = getRange(preferenceData?.stockExposure);
  const currencyRange = getRange(preferenceData?.currencyExposure);
  const foreignRange = getRange(preferenceData?.foreignExposure);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'right' }}>
        תכנון חשיפות משוקללות
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            <Tooltip title="הפעל גרירה לחשיפה למניות">
              <IconButton
                onClick={(e) => { e.stopPropagation(); onMetricClick?.('stock_market_percent'); }}
                color={activeMetric === 'stock_market_percent' ? "primary" : "default"}
                size="small"
                sx={{
                  border: activeMetric === 'stock_market_percent' ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: activeMetric === 'stock_market_percent' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="textSecondary">
                חשיפה למניות
              </Typography>
              <Typography variant="h5" sx={{
                fontWeight: 600,
                color: 'text.primary',
                backgroundColor: hasStockChanges ? 'rgba(0, 188, 212, 0.15)' : 'transparent',
                borderRadius: '4px',
                px: hasStockChanges ? 0.5 : 0,
                transition: 'background-color 0.3s',
              }}>
                {(stockExposure * 100).toFixed(1)}%
              </Typography>
              {renderChangeStatus('stock_market_percent', stockExposure * 100)}
            </Box>
          </Box>
          {preferenceData && onPreferenceChange && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                טווח העדפה
              </Typography>
              <MiniDoubleSlider
                min={0}
                max={100}
                rangeMin={stockRange.min}
                rangeMax={stockRange.max}
                onChange={handleStockRangeChange}
                step={10}
                width={100}
                disabled={!isDragEnabled}
                currentValue={(originalStockExposure ?? stockExposure) * 100}
                currentValueColor={
                  (originalStockExposure ?? stockExposure) * 100 >= stockRange.min &&
                  (originalStockExposure ?? stockExposure) * 100 <= stockRange.max
                    ? '#4CAF50'
                    : '#f44336'
                }
              />
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem', direction: 'ltr', textAlign: 'right' }}>
                {stockRange.min}% - {stockRange.max}%
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            <Tooltip title='הפעל גרירה לחשיפה למט"ח'>
              <IconButton
                onClick={(e) => { e.stopPropagation(); onMetricClick?.('foreign_currency_exposure_percent'); }}
                color={activeMetric === 'foreign_currency_exposure_percent' ? "primary" : "default"}
                size="small"
                sx={{
                  border: activeMetric === 'foreign_currency_exposure_percent' ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: activeMetric === 'foreign_currency_exposure_percent' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="textSecondary">
                חשיפה למט"ח
              </Typography>
              <Typography variant="h5" sx={{
                fontWeight: 600,
                color: 'text.primary',
                backgroundColor: hasCurrencyChanges ? 'rgba(0, 188, 212, 0.15)' : 'transparent',
                borderRadius: '4px',
                px: hasCurrencyChanges ? 0.5 : 0,
                transition: 'background-color 0.3s',
              }}>
                {(currencyExposure * 100).toFixed(1)}%
              </Typography>
              {renderChangeStatus('foreign_currency_exposure_percent', currencyExposure * 100)}
            </Box>
          </Box>
          {preferenceData && onPreferenceChange && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                טווח העדפה
              </Typography>
              <MiniDoubleSlider
                min={0}
                max={100}
                rangeMin={currencyRange.min}
                rangeMax={currencyRange.max}
                onChange={handleCurrencyRangeChange}
                step={10}
                width={100}
                disabled={!isDragEnabled}
                currentValue={(originalCurrencyExposure ?? currencyExposure) * 100}
                currentValueColor={
                  (originalCurrencyExposure ?? currencyExposure) * 100 >= currencyRange.min &&
                  (originalCurrencyExposure ?? currencyExposure) * 100 <= currencyRange.max
                    ? '#4CAF50'
                    : '#f44336'
                }
              />
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem', direction: 'ltr', textAlign: 'right' }}>
                {currencyRange.min}% - {currencyRange.max}%
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            <Tooltip title='הפעל גרירה לחשיפה לחו"ל'>
              <IconButton
                onClick={(e) => { e.stopPropagation(); onMetricClick?.('foreign_exposure_percent'); }}
                color={activeMetric === 'foreign_exposure_percent' ? "primary" : "default"}
                size="small"
                sx={{
                  border: activeMetric === 'foreign_exposure_percent' ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: activeMetric === 'foreign_exposure_percent' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="textSecondary">
                חשיפה לחו"ל
              </Typography>
              <Typography variant="h5" sx={{
                fontWeight: 600,
                color: 'text.primary',
                backgroundColor: hasForeignChanges ? 'rgba(0, 188, 212, 0.15)' : 'transparent',
                borderRadius: '4px',
                px: hasForeignChanges ? 0.5 : 0,
                transition: 'background-color 0.3s',
              }}>
                {(foreignExposure * 100).toFixed(1)}%
              </Typography>
              {renderChangeStatus('foreign_exposure_percent', foreignExposure * 100)}
            </Box>
          </Box>
          {preferenceData && onPreferenceChange && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                טווח העדפה
              </Typography>
              <MiniDoubleSlider
                min={0}
                max={100}
                rangeMin={foreignRange.min}
                rangeMax={foreignRange.max}
                onChange={handleForeignRangeChange}
                step={10}
                width={100}
                disabled={!isDragEnabled}
                currentValue={(originalForeignExposure ?? foreignExposure) * 100}
                currentValueColor={
                  (originalForeignExposure ?? foreignExposure) * 100 >= foreignRange.min &&
                  (originalForeignExposure ?? foreignExposure) * 100 <= foreignRange.max
                    ? '#4CAF50'
                    : '#f44336'
                }
              />
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem', direction: 'ltr', textAlign: 'right' }}>
                {foreignRange.min}% - {foreignRange.max}%
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default PathDragExposureSummary;
